import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

/**
 * `Chip` — the Chip/secondary pattern from the design system §2.
 *
 * Visual spec (Chip/secondary):
 *   `border-white/[0.12] bg-white/[0.06] text-white/72`
 *   hover brightens border and text.
 *
 * This is the same surface language as `Button` `secondary` but as a
 * display/label element (not a click target by default). Use the
 * `interactive` variant when the chip is itself a click target
 * (e.g. a removable tag chip).
 *
 * Element choice is semantic:
 *   - `default` (display) renders a `<span>`.
 *   - `interactive` renders a real `<button type="button">` so focus,
 *     Enter/Space activation, and screen-reader semantics all work
 *     natively — no manual `tabIndex`/`role`/`onKeyDown` needed. It
 *     bakes in `app-region-no-drag` so the Electron drag-region
 *     doesn't eat clicks.
 *
 * Non-interactive usage (the common display case):
 *
 *   <Chip>Genre: Trivia</Chip>
 *
 * Interactive usage (chip that can be pressed/removed) — renders a
 * `<button>`, so accepts button props (onClick, disabled, aria-label…):
 *
 *   <Chip variant="interactive" onClick={handleRemove} aria-label="Remove branch">
 *     my-branch <X size={10} />
 *   </Chip>
 */
const chipVariants = cva(
  'inline-flex items-center gap-1 rounded-full border border-white/[0.12] bg-white/[0.06] text-[11px] font-medium leading-none text-white/72 transition-colors',
  {
    variants: {
      variant: {
        /** Display label — no interaction styles, no drag-region opt-out. */
        default: '',
        /**
         * Clickable chip — hover brightens border and text; bakes in
         * `app-region-no-drag` so Electron drag-region doesn't eat clicks.
         * Rendered as a real `<button>`, so focus + Enter/Space are native.
         */
        interactive:
          'app-region-no-drag cursor-pointer hover:border-white/[0.24] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-crucible-accent/60 disabled:pointer-events-none disabled:opacity-40',
      },
      size: {
        default: 'px-2.5 py-1',
        sm: 'px-2 py-0.5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

type ChipVariant = NonNullable<VariantProps<typeof chipVariants>['variant']>;
type ChipSize = VariantProps<typeof chipVariants>['size'];

/** Non-interactive (display) chip — a `<span>`. */
interface DisplayChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: Extract<ChipVariant, 'default'>;
  size?: ChipSize;
}

/** Interactive chip — a real `<button type="button">`. */
interface InteractiveChipProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant: Extract<ChipVariant, 'interactive'>;
  size?: ChipSize;
}

export type ChipProps = DisplayChipProps | InteractiveChipProps;

function isInteractive(props: ChipProps): props is InteractiveChipProps {
  return props.variant === 'interactive';
}

export function Chip(props: ChipProps): React.JSX.Element {
  if (isInteractive(props)) {
    const { variant, size, className, children, type, ...rest } = props;
    return (
      <button
        // Default to type="button" so a chip nested in a form doesn't
        // submit it (bare <button> defaults to "submit").
        type={type ?? 'button'}
        className={cn(chipVariants({ variant, size }), className)}
        {...rest}
      >
        {children}
      </button>
    );
  }
  const { variant, size, className, children, ...rest } = props;
  return (
    <span className={cn(chipVariants({ variant, size }), className)} {...rest}>
      {children}
    </span>
  );
}

export { chipVariants };
